import { useMemo } from "react";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";

// Intl.supportedValuesOf is ES2022 runtime but not in the configured TS lib, so
// access it through a narrow cast rather than widening lib in tsconfig.
function listTimeZones(): string[] {
  const fn = (Intl as { supportedValuesOf?: (key: "timeZone") => string[] })
    .supportedValuesOf;
  try {
    return fn ? fn("timeZone") : [];
  } catch {
    return [];
  }
}

interface Props {
  value: string | null;
  onValueChange: (tz: string | null) => void;
  id?: string;
  className?: string;
  /** Portal target for the popup — pass the dialog content when used inside a modal. */
  container?: HTMLElement | null;
}

export function TimezoneCombobox({
  value,
  onValueChange,
  id,
  className,
  container,
}: Props) {
  // Always include the current value so a preset (or legacy) zone stays
  // selectable even if the runtime list omits it.
  const zones = useMemo(() => {
    const all = listTimeZones();
    if (value && !all.includes(value)) return [value, ...all];
    return all;
  }, [value]);

  return (
    <Combobox
      items={zones}
      value={value}
      onValueChange={(tz: string | null) => onValueChange(tz)}
      itemToStringLabel={(tz: string) => tz}
      itemToStringValue={(tz: string) => tz}
    >
      <ComboboxInput
        id={id}
        placeholder="Search timezone…"
        className={className ?? "w-full"}
      />
      <ComboboxContent container={container}>
        <ComboboxEmpty>No timezone found.</ComboboxEmpty>
        <ComboboxList>
          {(tz: string) => (
            <ComboboxItem key={tz} value={tz}>
              {tz}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}
